// src/components/checkout/ContactForm.jsx
import React from "react";
import useCheckoutStore from "../../store/useCheckoutStore";
import { sendEmailOtp, verifyEmailOtp } from "../../services/verifyService";
import { upsertUserProfile } from "../../services/usersService";

// --- styles (single S) ---
const S = {
  row: { display: "flex", gap: 12 },
  full: { width: "100%" },
  mt6: { marginTop: 6 },
  verifiedText: { color: "var(--color-primary)", fontSize: 13 },
  hint: {
    marginBottom: 8, padding: "8px 12px", borderRadius: 8,
    background: "#f5fdf9", border: "1px solid #cfeee3",
    color: "#055e47", fontSize: 13,
  },
  inputGroup: { display: "flex", gap: 8, alignItems: "center" },
  smallBtn: {
    whiteSpace: "nowrap", height: 36, padding: "0 10px",
    borderRadius: 8, border: "1px solid var(--color-primary)",
    background: "var(--color-primary)", color: "#fff",
    fontSize: 13, cursor: "pointer",
  },
  smallBtnGhost: {
    whiteSpace: "nowrap", height: 36, padding: "0 10px",
    borderRadius: 8, border: "1px solid #ddd",
    background: "#fff", color: "#333",
    fontSize: 13, cursor: "pointer",
  },
  pill: {
    display: "inline-flex", alignItems: "center", gap: 6,
    height: 28, padding: "0 10px",
    borderRadius: 999, background: "#eefaf4",
    border: "1px solid #cfeee3", color: "#055e47", fontSize: 12,
  },
  codeBox: { display: "flex", gap: 8, marginTop: 6 },
  codeInput: { flex: 1, height: 36, borderRadius: 8, padding: "0 10px" },
};

const POLAND_PREFIX = "+48";
const COUNTDOWN = 60;

const ContactForm = ({ user, setFormValid, onUserReady, shopId, deviceId }) => {
  const isAnonymous = !user || user.isAnonymous === true;

  const { checkoutData, updateCheckoutData } = useCheckoutStore();
  const { contact = {}, address, payment, method, pickupTimeType, scheduledPickup } = checkoutData;

  // --- local state for email OTP flow ---
  const [email, setEmail] = React.useState(contact.email || "");
  const [emailCode, setEmailCode] = React.useState("");
  const [showEmailOtp, setShowEmailOtp] = React.useState(false);
  const [sendingEmail, setSendingEmail] = React.useState(false);
  const [verifyingEmail, setVerifyingEmail] = React.useState(false);
  const [emailTimer, setEmailTimer] = React.useState(0);

  // resend countdown
  React.useEffect(() => {
    if (!emailTimer) return;
    const t = setInterval(() => setEmailTimer((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [emailTimer]);

  const validateEmail = (v) => /\S+@\S+\.\S+/.test(v);
  const validatePLPhone = (v) => v?.startsWith(POLAND_PREFIX) && v?.length === 12;

  // save profile after email is verified (guest or signed-in)
  const trySaveAndNotify = React.useCallback(async () => {
    try {
      if (contact.emailVerified === true) {
        const payload = {
          ...contact,
          isGuest: isAnonymous,
          shopId,
          deviceId:
            deviceId ||
            (typeof window !== "undefined" && localStorage.getItem("deviceId")) ||
            null,
        };
        const { uid, name } = await upsertUserProfile(payload, email);
        onUserReady?.({ uid, name, isGuest: isAnonymous });
      }
    } catch (e) {
      console.warn("Profile upsert skipped:", e?.message || e);
    }
  }, [isAnonymous, contact, email, onUserReady, shopId, deviceId]);

  // phone formatter (+48XXXXXXXXX)
  const handlePhoneChange = (e) => {
    let input = e.target.value.replace(/\D/g, "");
    if (input.startsWith("48")) input = input.slice(2);
    input = input.slice(0, 9);
    const phone = `${POLAND_PREFIX}${input}`;
    updateCheckoutData({ contact: { ...contact, phone } });
  };

  const handleEmailChange = (e) => {
    const v = e.target.value.trim();
    setEmail(v);
    updateCheckoutData({ contact: { ...contact, email: v, emailVerified: false } });
    setEmailCode("");
  };

  const onSendEmailOtp = async () => {
    if (!validateEmail(email)) return alert("Podaj poprawny email.");
    try {
      setSendingEmail(true);
      await sendEmailOtp(email);
      setShowEmailOtp(true);
      setEmailTimer(COUNTDOWN);
    } catch {
      alert("Nie udało się wysłać kodu email.");
    } finally {
      setSendingEmail(false);
    }
  };

  const onVerifyEmailOtp = async () => {
    if (!emailCode) return;
    try {
      setVerifyingEmail(true);
      const ok = await verifyEmailOtp(email, emailCode);
      if (!ok) return alert("Niepoprawny kod email.");
      updateCheckoutData({ contact: { ...contact, email, emailVerified: true } });
      setShowEmailOtp(false);
      trySaveAndNotify();
    } catch {
      alert("Weryfikacja email nie powiodła się.");
    } finally {
      setVerifyingEmail(false);
    }
  };

  // --- validations for step 1 + overall formValid back to parent ---
  const nameOk = !!contact.firstName?.trim();
  const emailOk = validateEmail(email);
  const emailVerifiedOk = contact.emailVerified === true;

  const addressOk =
    method === "delivery"
      ? address?.street && address?.city && /^\d{2}-\d{3}$/.test(address?.postcode) && !!payment
      : method === "pickup" && (pickupTimeType === "asap" || scheduledPickup);

  const requirePhone = !isAnonymous;
  const phoneOk = requirePhone ? validatePLPhone(contact.phone) : true;

  const isFormValid = nameOk && emailOk && emailVerifiedOk && phoneOk && addressOk;

  React.useEffect(() => {
    setFormValid?.(!!isFormValid);
  }, [isFormValid, setFormValid]);

  return (
    <div className="card">
      <h2>Dane kontaktowe</h2>

      {isAnonymous && (
        <div style={S.hint}>
          Tryb gościa: aby kontynuować, wystarczy <b>zweryfikować adres e-mail</b>.{" "}
          {/* <button
            type="button"
            style={{ ...S.smallBtn, marginLeft: 8 }}
            onClick={() => (window.location.href = "/login")}
          >
            Zaloguj się
          </button> */}
        </div>
      )}

      {/* Full name */}
      <input
        type="text"
        placeholder="Imię i nazwisko"
        value={contact.firstName || ""}
        onChange={(e) => updateCheckoutData({ contact: { ...contact, firstName: e.target.value } })}
      />

      <div className="input-row" style={S.row}>
        {/* Phone (plain input, no OTP) */}
        <div style={{ flex: 1 }}>
          <input
            type="tel"
            placeholder="+48 123 456 789"
            value={contact.phone || ""}
            onChange={handlePhoneChange}
            required={requirePhone}
          />
        </div>

        {/* Email + compact actions */}
        <div style={{ flex: 1 }}>
          {/* Email input with small action button/pill inline */}
          <div style={S.inputGroup}>
            <input
              type="email"
              placeholder="jan.kowalski@example.com"
              value={email}
              onChange={handleEmailChange}
              disabled={contact.emailVerified}
              style={{ flex: 1 }}
            />

            {contact.emailVerified ? (
              // Small green pill with check icon
              <span style={S.pill} title="Email zweryfikowany">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path d="M20 6L9 17l-5-5" stroke="#0a8f66" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Zweryfikowano
              </span>
            ) : (
              // Tiny "send code" button
              <button
                type="button"
                onClick={onSendEmailOtp}
                disabled={sendingEmail || emailTimer > 0 || !validateEmail(email)}
                style={S.smallBtn}
              >
                {emailTimer > 0 ? `Wyślij ponownie (${emailTimer}s)` : "Wyślij kod"}
              </button>
            )}
          </div>

          {/* Compact OTP confirm row (only while verifying) */}
          {showEmailOtp && !contact.emailVerified && (
            <div style={S.codeBox}>
              <input
                type="text"
                placeholder="Kod z e-maila"
                value={emailCode}
                onChange={(e) => setEmailCode(e.target.value.trim())}
                maxLength={6}
                inputMode="numeric"
                pattern="[0-9]*"
                style={S.codeInput}
              />
              <button
                type="button"
                onClick={onVerifyEmailOtp}
                disabled={!emailCode || verifyingEmail}
                style={S.smallBtnGhost}
              >
                {verifyingEmail ? "Weryfikuję…" : "Potwierdź"}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Subtle verified text (optional – can remove if pill is enough) */}
      {/* {contact.emailVerified && (
        <p style={{ ...S.verifiedText, marginTop: 6 }}>Email zweryfikowany ✅</p>
      )} */}
    </div>
  );
};

export default ContactForm;
